import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage } from "@/components/site/LegalPage";
import { COMPANY } from "@/lib/company";

const URL_ = "https://www.investcapital.pt/politica-de-privacidade";

export const Route = createFileRoute("/politica-de-privacidade")({
  component: Page,
  head: () => ({
    meta: [
      { title: "Política de Privacidade — InvestCapital" },
      { name: "description", content: "Como a InvestCapital recolhe, usa, partilha e protege os seus dados pessoais, e como exercer os seus direitos (RGPD)." },
      { property: "og:title", content: "Política de Privacidade — InvestCapital" },
      { property: "og:description", content: "Tratamento de dados pessoais na InvestCapital, partilhas e direitos do titular." },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.investcapital.pt/og-investcapital.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://www.investcapital.pt/og-investcapital.jpg" },
      { property: "og:url", content: URL_ },
    ],
    links: [{ rel: "canonical", href: URL_ }],
  }),
});

function Page() {
  return (
    <LegalPage title="Política de Privacidade" updated="26 de outubro de 2026">

      <h2>1. Responsável pelo tratamento</h2>
      <p>
        {COMPANY.legalName} ({COMPANY.brand}), {COMPANY.taxId ? `${COMPANY.taxLabel} ${COMPANY.taxId}, ` : ""}{COMPANY.addressLabel.toLowerCase()}: {COMPANY.address}.
        Email: <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> · Tel./WhatsApp: {COMPANY.phone}.
      </p>

      <h2>2. Dados que recolhemos</h2>
      <ul>
        <li>Dados de identificação e contacto: nome, telefone, e-mail.</li>
        <li>Dados do imóvel: tipo, localização (concelho), estado, mensagem e ficheiros que decida anexar (fotografias, caderneta predial, plantas, documentos).</li>
        <li>Dados de navegação, apenas com o seu consentimento: cookies e identificadores de estatística e marketing (ver <Link to="/politica-de-cookies">Política de Cookies</Link>), endereço IP e tipo de browser.</li>
      </ul>

      <h2>3. Finalidades e base legal</h2>
      <table>
        <thead><tr><th>Finalidade</th><th>Base legal (RGPD)</th></tr></thead>
        <tbody>
          <tr><td>Responder ao seu pedido, analisar o imóvel e apresentar proposta de compra</td><td>Diligências pré-contratuais a seu pedido (art. 6.º, n.º 1, b)) e consentimento (art. 6.º, n.º 1, a))</td></tr>
          <tr><td>Partilha com investidores parceiros para avaliação e apresentação de proposta</td><td>Consentimento (art. 6.º, n.º 1, a))</td></tr>
          <tr><td>Estatísticas de utilização do site (Google Analytics)</td><td>Consentimento (art. 6.º, n.º 1, a))</td></tr>
          <tr><td>Medição de anúncios (Meta Pixel, API de Conversões, Google)</td><td>Consentimento (art. 6.º, n.º 1, a))</td></tr>
          <tr><td>Cumprimento de obrigações legais e defesa de direitos</td><td>Obrigação legal e interesse legítimo (art. 6.º, n.º 1, c) e f))</td></tr>
        </tbody>
      </table>

      <h2>4. Prazo de conservação</h2>
      <ul>
        <li>Pedidos de proposta sem negócio concretizado: até 24 meses após o último contacto.</li>
        <li>Dados de negócios concretizados: pelo prazo legal aplicável (ex.: 10 anos para documentação fiscal e contabilística).</li>
        <li>Ficheiros anexados: eliminados até 24 meses após o último contacto, se não houver negócio.</li>
        <li>Cookies: ver prazos na <Link to="/politica-de-cookies">Política de Cookies</Link>.</li>
      </ul>

      <h2>5. Partilha com terceiros</h2>
      <ul>
        <li><strong>Investidores parceiros</strong>: os dados do imóvel e de contacto podem ser partilhados com investidores parceiros, exclusivamente para avaliação do imóvel e apresentação de proposta, sujeitos a dever de confidencialidade.</li>
        <li><strong>Meta Platforms Ireland Ltd.</strong> (Facebook/Instagram): com consentimento de marketing, dados de navegação e, no envio do formulário, e-mail e telefone cifrados (hash) para medição de anúncios.</li>
        <li><strong>Google Ireland Ltd.</strong> (Google Analytics, Tag Manager, Google Ads): com consentimento, dados de navegação para estatística e medição de anúncios.</li>
        <li><strong>Prestadores técnicos</strong> (alojamento, base de dados e envio de e-mail), que atuam como subcontratantes.</li>
      </ul>
      <p>
        Alguns destes destinatários podem transferir dados para fora do Espaço Económico Europeu (ex.: EUA), com base em decisões de adequação
        (EU-US Data Privacy Framework) ou cláusulas contratuais-tipo da Comissão Europeia.
      </p>

      <h2>6. Os seus direitos</h2>
      <p>Pode, a qualquer momento e gratuitamente, exercer os direitos de:</p>
      <ul>
        <li>acesso aos seus dados;</li>
        <li>retificação;</li>
        <li>apagamento;</li>
        <li>limitação do tratamento;</li>
        <li>portabilidade;</li>
        <li>oposição;</li>
        <li>retirar o consentimento, sem afetar a licitude do tratamento anterior. Para cookies, use “Definições de cookies” no rodapé.</li>
      </ul>
      <p>
        Para exercer estes direitos, escreva para <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>. Tem ainda o direito de apresentar
        reclamação à Comissão Nacional de Proteção de Dados (CNPD), em <a href="https://www.cnpd.pt" target="_blank" rel="noopener noreferrer">www.cnpd.pt</a>.
      </p>

      <h2>7. Segurança</h2>
      <p>
        Os pedidos são guardados numa base de dados com acesso restrito ao responsável. Os ficheiros ficam num arquivo privado, e os links de acesso
        enviados internamente expiram ao fim de 7 dias.
      </p>

      <h2>8. Alterações</h2>
      <p>Esta política pode ser atualizada. A data da última atualização está indicada no topo.</p>
    </LegalPage>
  );
}
